/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';
import { useTheme } from 'emotion-theming';

import { Theme, composeStyles } from 'features/Theme';

import styles from './styles';
import React, { useMemo } from 'react';
import useFeature from 'hooks/useFeature';
import { selectCreditApplication } from 'state/creditApplication';
import ImageWithLoadSpinner from 'components/ImageWithLoadSpinner';
import { selectCachedAttributes } from 'state/cachedAttributes';
import { selectForceTenantLogo } from 'state/tenantLogo';

interface PropTypes {
  className?: string;
}

/**
 * Logo at the top of the page.
 */
const Logo: React.FC<PropTypes> = ({ className }) => {
  const tenant = useSelector(selectTenant);
  const theme = useTheme<Theme>();
  const { selectedCardKey: cachedKey } = useSelector(selectCachedAttributes);
  const { selectedCardKey } = useSelector(selectCreditApplication);
  const forceTenantLogo = useSelector(selectForceTenantLogo);
  const { isEnabled } = useFeature();

  const key = cachedKey || selectedCardKey;

  const cardSelector = tenant.enabledFeatures.cardSelector;
  const card = useMemo(() => {
    return (
      isEnabled('cardSelector') &&
      cardSelector &&
      cardSelector.options.find(option => option.key === key)
    );
  }, [cardSelector, isEnabled, key]);

  const showLogo = !theme.embedded && (forceTenantLogo || !card);
  const showCard = Boolean(card) && !showLogo;

  if (!showCard && !showLogo) {
    return null;
  }

  return (
    <React.Fragment>
      <div
        css={composeStyles([
          styles.logoContainer,
          showCard ? styles.cardContainer : null,
        ])}
        className={className}
      >
        {showCard && card ? (
          <ImageWithLoadSpinner
            imgCss={styles.logo}
            spinnerCss={styles.spinner}
            src={card.imageUrl}
            alt={card.name}
          />
        ) : null}
        {showLogo ? <img src={tenant.logoUrl} alt={tenant.name} /> : null}
      </div>
    </React.Fragment>
  );
};

export default Logo;
